import { useCallback, useEffect, useState } from "react"
import { supabase } from "./supabase"
import { useAuth } from "../components/AuthProvider"

export type ProfileDisplay = {
  name: string | null
  email: string
  phone: string | null
  subtitle: string
  /** Profile/account photo URL (client: logo_url; admin/operator: user_metadata.avatar_url). */
  avatarUrl: string | null
  /** Client only: company/organization name. */
  companyName?: string | null
  /** Client only: account holder / primary contact name. */
  accountHolderName?: string | null
}

/**
 * Single source for logged-in user profile (name, email, phone).
 * Use in TopBar ("Hello, {name}") and Settings so they stay in sync.
 * refetch() after updating name/phone in Settings to sync everywhere.
 */
export function useProfile(): { profile: ProfileDisplay | null; loading: boolean; refetch: () => Promise<void> } {
  const { user, clientId, role } = useAuth()
  const [profile, setProfile] = useState<ProfileDisplay | null>(null)
  const [loading, setLoading] = useState(true)

  const resolveProfile = useCallback(async () => {
      if (!user?.email) {
        setLoading(false)
        return
      }
      try {
        if (role === "client" && clientId) {
          const { data } = await supabase
            .from("clients")
            .select("name, email, phone, primary_contact, logo_url")
            .eq("id", clientId)
            .single()
          if (data) {
            const companyName = data.name ?? null
            const accountHolderName = (data as { primary_contact?: string }).primary_contact ?? null
            setProfile({
              name: accountHolderName ?? companyName ?? null,
              email: data.email ?? user.email,
              phone: data.phone ?? null,
              subtitle: "Primary Client Account",
              avatarUrl: (data as { logo_url?: string | null }).logo_url ?? null,
              companyName,
              accountHolderName,
            })
          } else {
            setProfile({
              name: null,
              email: user.email,
              phone: null,
              subtitle: "Primary Client Account",
              avatarUrl: null,
            })
          }
        } else if (role === "admin" || role === "creator") {
          setProfile({
            name: (user.user_metadata?.full_name as string) ?? (user.user_metadata?.name as string) ?? null,
            email: user.email,
            phone: (user.user_metadata?.phone as string) ?? null,
            subtitle: "Admin account",
            avatarUrl: (user.user_metadata?.avatar_url as string) ?? null,
          })
        } else if (role === "operator") {
          const { data: operatorRow } = await (supabase as any)
            .from("portal_operators")
            .select("full_name, email")
            .eq("email", user.email)
            .maybeSingle()
          setProfile({
            name:
              operatorRow?.full_name ??
              (user.user_metadata?.full_name as string) ??
              (user.user_metadata?.name as string) ??
              null,
            email: user.email,
            phone: (user.user_metadata?.phone as string) ?? null,
            subtitle: "Operator account",
            avatarUrl: (user.user_metadata?.avatar_url as string) ?? null,
          })
        } else {
          setProfile({
            name: (user.user_metadata?.full_name as string) ?? null,
            email: user.email,
            phone: null,
            subtitle: "Account",
            avatarUrl: (user.user_metadata?.avatar_url as string) ?? null,
          })
        }
      } catch (error) {
        console.error("Error resolving profile:", error)
        setProfile({
          name: null,
          email: user.email,
          phone: null,
          subtitle: "Account",
          avatarUrl: null,
        })
      } finally {
        setLoading(false)
      }
  }, [user, role, clientId])

  useEffect(() => {
    resolveProfile()
  }, [resolveProfile])

  const refetch = useCallback(async () => {
    setLoading(true)
    await resolveProfile()
  }, [resolveProfile])

  return { profile, loading, refetch }
}
